import { useCallback, useMemo, useState } from 'react';
import type { ComboboxOption } from '@library/components/selects';
import { useGetLegacyRulesetsQuery } from '@bo-services/rulesetsApi';
import { useUpdateMemberMutation } from '@bo-services/members/membersApi';

export interface MemberRulesetsProps {
  memberId: string;
  memberRulesetId?: number;
  memberRulesetName?: string;
}

export const useMemberLegacyRulesets = ({
  memberId,
  memberRulesetId,
  memberRulesetName
}: MemberRulesetsProps) => {
  const initialSelectedRuleset: ComboboxOption | null =
    memberRulesetId && memberRulesetName
      ? { label: memberRulesetName, value: memberRulesetId }
      : null;

  const [selectedRuleset, setSelectedRuleset] = useState<ComboboxOption | null>(
    initialSelectedRuleset
  );

  const [
    updateMember,
    {
      isLoading: isSubmitLoading,
      isError: isSubmitError,
      isSuccess: isSubmitSuccess
    }
  ] = useUpdateMemberMutation();

  const { data: rulesetData, isFetching } = useGetLegacyRulesetsQuery({
    omit_users: true,
    page: 1,
    per_page: 1000
  });

  const memberRulesetOptions = useMemo(() => {
    if (!rulesetData?.length) {
      return [];
    }

    return rulesetData
      .map(({ attributes, id }) => ({ label: attributes.name, value: id }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [rulesetData]);

  const handleSubmit = useCallback(async () => {
    if (!memberId || !selectedRuleset?.value) {
      throw new Error('Member ID and Ruleset ID are required');
    }

    await updateMember({
      member_id: memberId,
      ruleset_id: Number(selectedRuleset.value)
    }).unwrap();
  }, [memberId, selectedRuleset, updateMember]);

  const isLoading = isFetching || isSubmitLoading;

  return {
    isLoading,
    isSubmitError,
    isSubmitSuccess,
    memberRulesetOptions,
    onRulesetChange: setSelectedRuleset,
    onSubmit: handleSubmit,
    selectedRuleset
  };
};
